/**
 * Signed-URL resolver (IMPL-17). The doc stores stable `lapo://` references; private blobs are
 * readable only via short-lived signed URLs minted by an authenticated endpoint. NodeViews call
 * this resolver to turn a reference into a usable URL. It caches by reference and refreshes before
 * expiry, and dedupes concurrent fetches for the same reference so a page full of images issues one
 * request per distinct blob. The transport (`fetchSigned`) is injected, so the cache/expiry logic
 * is pure and unit-testable without a network.
 */

export interface SignedUrl {
  url: string;
  /** epoch ms when the signed URL stops working. */
  expiresAt: number;
}

export interface SignedUrlResolver {
  resolve(uri: string): Promise<string>;
}

export interface SignedUrlResolverOptions {
  fetchSigned: (uri: string) => Promise<SignedUrl>;
  /** injectable clock for tests; defaults to Date.now. */
  now?: () => number;
  /** refresh this long before expiry to avoid handing out an about-to-die URL. */
  skewMs?: number;
}

export function createSignedUrlResolver(opts: SignedUrlResolverOptions): SignedUrlResolver {
  const now = opts.now ?? (() => Date.now());
  const skewMs = opts.skewMs ?? 60_000;
  const cache = new Map<string, SignedUrl>();
  const inflight = new Map<string, Promise<string>>();

  const isFresh = (entry: SignedUrl): boolean => now() < entry.expiresAt - skewMs;

  return {
    resolve(uri: string): Promise<string> {
      const cached = cache.get(uri);
      if (cached && isFresh(cached)) return Promise.resolve(cached.url);

      const existing = inflight.get(uri);
      if (existing) return existing;

      const pending = opts
        .fetchSigned(uri)
        .then((signed) => {
          cache.set(uri, signed);
          inflight.delete(uri);
          return signed.url;
        })
        .catch((err) => {
          inflight.delete(uri);
          throw err;
        });
      inflight.set(uri, pending);
      return pending;
    },
  };
}
